/**
 * DiscardChangesDialog
 *
 * Confirmation dialog for discarding all local offline changes.
 * This is an irreversible action that clears all pending mutations and resets to server state.
 */

import { FC, useState } from 'react';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SxProps,
  Theme,
} from '@mui/material';

import { useTranslation } from 'common/hooks/useTranslation';

export type DiscardChangesDialogProps = {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** Called when user confirms the discard action */
  onConfirm: () => Promise<void>;
  /** Number of pending changes that will be discarded */
  pendingCount: number;
  /** Number of failed changes that will be discarded */
  failedCount: number;
};

const dialogStyles: SxProps<Theme> = {
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: 400,
  },
};

const titleStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  color: 'error.main',
};

const warningIconStyles: SxProps<Theme> = {
  color: 'warning.main',
  fontSize: 28,
};

const actionsStyles: SxProps<Theme> = {
  padding: 2,
  gap: 1,
};

export const DiscardChangesDialog: FC<DiscardChangesDialogProps> = ({
  open,
  onClose,
  onConfirm,
  pendingCount,
  failedCount,
}) => {
  const { t } = useTranslation();
  const [isDiscarding, setIsDiscarding] = useState(false);

  const totalChanges = pendingCount + failedCount;

  const handleConfirm = async () => {
    setIsDiscarding(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsDiscarding(false);
    }
  };

  return (
    <Dialog open={open} onClose={isDiscarding ? undefined : onClose} sx={dialogStyles}>
      <DialogTitle sx={titleStyles}>
        <WarningAmberIcon sx={warningIconStyles} />
        {t('sync.discard.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{t('sync.discard.message', { count: totalChanges })}</DialogContentText>
        <DialogContentText sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
          {t('sync.discard.warning')}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={actionsStyles}>
        <Button variant="outlined" onClick={onClose} disabled={isDiscarding}>
          {t('general.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isDiscarding}
          startIcon={isDiscarding ? <CircularProgress size={16} color="inherit" /> : <DeleteForeverIcon />}
        >
          {isDiscarding ? t('sync.discard.discarding') : t('sync.discard.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
